import { useState } from 'react';
import { Modal } from 'antd';
import {
  Input,
  Checkbox,
  Button,
  Flex,
  Select,
  message as messageApi,
  Form,
} from 'antd';
import { useMutation } from '@apollo/client/react';

import useAgent from 'hooks/useAgent';
import useFraudTemplates from 'hooks/useFraudTemplates';
import { ADD_NOTES_MUTATION } from 'mutations/notes';
import FraudTemplateFields from 'components/FraudTemplateFields/FraudTemplateFields';

const { TextArea } = Input;
const { Item } = Form;

const defaultProduct = 'CREDIT';

const AddNotes = ({ hide, ...modalProps }) => {
  const [form] = Form.useForm();
  const agent = useAgent();

  const [saved, setSaved] = useState(false);

  const fraudTemplateForm = useFraudTemplates(form, 'note');

  const [addNotesMutation, { loading }] = useMutation(ADD_NOTES_MUTATION, {
    onCompleted: () => {
      setSaved(true);
      clearFields();
    },
    onError: (error) => {
      messageApi.error(error.message);
    },
  });

  const saveNote = () => {
    const values = form.getFieldsValue(true);
    const customerIds = values.customerIds?.split(',').map((id) => {
      return id?.replace(/\D/g, '');
    });

    addNotesMutation({
      variables: {
        applicationIds: [],
        customerIds,
        message: values.note,
        isSticky: values.createFraudSticky || values.isSticky || false,
        agentId: agent?.user?.requester_id,
        agentName: agent?.user?.name,
        product: values.product,
        ...(values.createFraudSticky && { category: 'FRAUD' }),
      },
    });
  };

  const handleOnCancel = () => {
    clearFields();
    hide();
  };

  const clearFields = () => {
    form.resetFields();
    fraudTemplateForm.reset();
  };

  const handleCreateAnotherNote = () => {
    clearFields();
    setSaved(false);
  };

  const note = Form.useWatch('note', form);
  const customerIds = Form.useWatch('customerIds', form);

  return (
    <Modal
      {...modalProps}
      title="Add bulk note to multiple customer profiles"
      footer={null}
      width={650}
    >
      {!saved ? (
        <Form
          form={form}
          layout={'vertical'}
          size={'middle'}
          initialValues={{ product: defaultProduct, isSticky: false }}
        >
          {fraudTemplateForm.enabled && (
            <FraudTemplateFields
              fraudTemplateForm={fraudTemplateForm}
              testIdPrefix="bulk"
            />
          )}
          <Item name="note" label="Note">
            <TextArea
              data-testid="note-text-area"
              autoSize={{ minRows: 3, maxRows: 10 }}
              disabled={fraudTemplateForm.createFraudSticky}
            />
          </Item>
          <Item name="customerIds" label="Customer Ids">
            <TextArea
              data-testid="customer-ids-text-area"
              autoSize={{ minRows: 3, maxRows: 5 }}
              placeholder="123456789, 456782489, 8735137892, 873254264"
            />
          </Item>
          <Item name="product" label="Product">
            <Select
              data-testid="product-select"
              options={[
                { label: 'Credit', value: 'CREDIT' },
                { label: 'Money', value: 'DEBIT' },
                { label: 'Earn', value: 'GIGS' },
              ]}
            />
          </Item>
          {!fraudTemplateForm.createFraudSticky && (
            <Item name="isSticky" valuePropName="checked">
              <Checkbox data-testid="is-sticky-check-box">
                Super Sticky
              </Checkbox>
            </Item>
          )}
          <Item>
            <Flex justify="flex-end" gap="var(--spacing-xs)">
              <Button onClick={handleOnCancel}>Cancel</Button>
              <Button
                data-testid="save-note-button"
                type="primary"
                onClick={saveNote}
                loading={loading}
                disabled={!note || !customerIds}
              >
                Save
              </Button>
            </Flex>
          </Item>
        </Form>
      ) : (
        <Flex vertical gap="var(--spacing-lg)">
          <span style={{ fontSize: '16px' }}>Notes Saved Successfully!</span>
          <Flex justify="flex-end" gap="var(--spacing-xs)">
            <Button onClick={handleOnCancel}>Close</Button>
            <Button
              data-testid="save-note-button"
              type="primary"
              onClick={handleCreateAnotherNote}
            >
              Create another note
            </Button>
          </Flex>
        </Flex>
      )}
    </Modal>
  );
};

export default AddNotes;
